/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { act, useState } from "react";
import { IonButton, IonContent, IonIcon } from "@ionic/react";
import { chevronBack } from "ionicons/icons";
import NumberSelector from "../../ui/NumberSelector";
import MinutesPerTherapySlider from "../../ui/MinuteGoals";
import { getBrandedDeviceName } from "../../helpers/ui";
import analytics from "../../../services/firebaseAnalytics";

interface SetGoalsScreenProps {
  nextStep: (index?: number) => void;
  onBack: () => void;
  onNext: (device: string, timesPerDay: number, therapyDuration: any) => void;
  onSkip: () => void;
  deviceName?: string;
  devices?: string[];
  onChange?: (updatedGoal: any) => void;
  showNumbers?: boolean;
  subTitle?: string;
  subText?: string;
  activeDevice: string;
}

const SetGoalsScreen: React.FC<SetGoalsScreenProps> = ({
  nextStep,
  onBack,
  onNext,
  onSkip,
  deviceName = "BiWaze® Cough",
  devices = [],
  onChange,
  showNumbers = true,
  subTitle = "I plan to do therapy",
  subText = "",
  activeDevice,
}) => {
  // State for device selection
  // const [activeDevice, setActiveDevice] = useState(
  //   devices.length > 0 ? devices[0] : deviceName
  // );
  // Show minutes for clear device
  const showMinutes =
    typeof activeDevice === "string" &&
    activeDevice.toLowerCase().includes("clear");
  // State for goal tracking with default values
  const [timesPerDay, setTimesPerDay] = useState(2);
  const [therapyMinutes, setTherapyMinutes] = useState(10);
  const [stepGoal, setStepGoal] = useState(0);

  const handleChange = (field: string, value: any) => {
    // Update local state based on field
    if (field === "target_count") {
      setTimesPerDay(value);
    } else if (field === "target_seconds") {
      setTherapyMinutes(value);
    } else if (field === "target_steps") {
      setStepGoal(value);
    }

    // Create updated goal object with current state
    // Use the new value for the changed field, and current state for others
    const updatedGoal = {
      device: activeDevice,
      timesPerDay,
      therapyMinutes,
      stepGoal,
      isActive: true,
      target_count: field === "target_count" ? value : timesPerDay,
      target_seconds: field === "target_seconds" ? value : therapyMinutes * 60, // Convert minutes to seconds
      target_steps: field === "target_steps" ? value : stepGoal,
    };

    // Notify parent of change if callback provided
    if (onChange) {
      onChange(updatedGoal);
    }
  };
  const handleSave = () => {
    const goalData = {
      isActive: true,
      target_count: timesPerDay,
      target_seconds: therapyMinutes * 60, // Convert minutes to seconds
      target_steps: stepGoal,
    };

    onNext(activeDevice, timesPerDay, goalData);
  };

  // Log analytics event when component is mounted
  React.useEffect(() => {
    const logAnalyticsEvent = async () => {
      await analytics.logEvent("onboarding_goal_setting_viewed", {
        activeDevice: activeDevice,
      });
    };
    logAnalyticsEvent();
    // Only run on mount or when activeDevice changes
  }, [activeDevice]);

  return (
    <IonContent className="ion-padding onboarding-section">
      <div className="navigation-header">
        <button className="back-text-button" onClick={onBack}>
          <IonIcon icon={chevronBack} className="back-icon" />
          <span>Back</span>
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-center text-center pb-32 top-40 py-6">
        <h1
          className="font-bold mb-4"
          style={{
            height: "41px",
            fontFamily: "'Figtree', sans-serif",
            fontStyle: "normal",
            fontWeight: 700,
            fontSize: "32px",
            textAlign: "center",
            letterSpacing: "0.374px",
          }}
        >
          Set Goals
        </h1>

        {/* Current Device Display */}
        <div className="text-2xl font-bold text-black mb-2">
          {getBrandedDeviceName(activeDevice ?? "")}
        </div>

        {/* Goal Description */}
        <div className="text-xl font-normal text-black ">{subTitle}</div>

        {/* Number Selector for frequency */}
        {showNumbers && (
          <div className="flex flex-col items-center w-full justify-center">
            <div className="mb-3 flex justify-center">
              <NumberSelector
                values={[1, 2, 3, 4, 5]}
                defaultValue={timesPerDay}
                onChange={(value) => handleChange("target_count", value)}
                activeColor="#00A9CE"
                inactiveColor="#FFFFFF"
                activeTextColor="#FFFFFF"
                size={55}
                gap={10}
              />
            </div>
            <div className=" px-5 py-2 rounded-full font-medium text-center mx-auto mb-2">
              Times per day
            </div>
          </div>
        )}

        {/* Minutes Slider */}
        {showMinutes && (
          <MinutesPerTherapySlider
            onChange={(value: any) => {
              console.log("Therapy minutes changed:", value);
              setTherapyMinutes(+value);
              // handleChange("target_seconds", value);
            }}
          />
        )}

        {showMinutes && (
          <div
            className="flex flex-row justify-center items-center rounded-[40px] "
            style={{
              fontFamily: "'Figtree', sans-serif",
              fontStyle: "normal",
              fontWeight: 500,
              fontSize: "16px",
              lineHeight: "20px",
              textAlign: "center",
              color: "#000000",
              fontFeatureSettings: "'case' on",
            }}
          >
            <span className="text-md p-2">Minutes per therapy</span>
          </div>
        )}

        {/* Additional Text */}
        {subText && <p className="text-center mt-3">{subText}</p>}
      </div>

      {/* Bottom Buttons */}
      <div
        className="button-stack-container gap-4"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <IonButton
          expand="block"
          className="primary-button"
          onClick={handleSave}
        >
          Next
        </IonButton>
        <button className="skip-link" onClick={() => nextStep()}>
          Skip for now
        </button>
      </div>
    </IonContent>
  );
};

export default SetGoalsScreen;
